import { buildApiUrl } from '../../services/apiConfig';
import axios from 'axios';

const FRAUD_TYPOLOGY_COLLECTION = 'fraud_typology_docs';
const QUESTIONNAIRE_COLLECTION = 'claim_intake_questionnaire';

const FRAUD_KEYWORDS = [
  'stolen', 'fraud', 'fraudulent', 'unauthorized', 'suspicious', 'scam', 'phishing',
  'account takeover', 'debit card', 'credit card', 'card', 'transaction', 'charge',
  'claim', 'dispute', 'victim', 'theft', 'compromised', 'breach', 'hack', 'hacked',
  'payment', 'transfer', 'wire', 'atm', 'withdrawal', 'identity', 'impersonation',
  'pin', 'password reset', 'new device', 'beneficiary', 'merchant', 'chargeback',
  'account', 'bank', 'balance', 'overdraft', 'deposit', 'joint account', 'report',
];

class CustomerServiceDashboardService {
  constructor() {
    this.storedUserId = sessionStorage.getItem('user_id') || 'U00001';
    this.ragQueryCache = {};
  }

  hasFraudKeywords(text) {
    const lower = text.toLowerCase();
    return FRAUD_KEYWORDS.some(kw => lower.includes(kw));
  }

  // Fix common AI JSON formatting issues so JSON.parse can succeed
  repairJSON(text) {
    let s = text;

    // 1. Fix double-quoted string values: "key": ""value"" → "key": "value"
    s = s.replace(/(":\s*)""([^"]*)""/g, '$1"$2"');

    // 2. Remove spurious closing } the model emits between ] and more fields:
    //    e.g.  }],   },   next_field:  →  }],   next_field:
    //    Must run BEFORE trailing-comma removal so the comma between ] and } is still present.
    s = s.replace(/\]\s*,\s*\}\s*,/g, '],');

    // 3. Remove trailing commas before } or ]
    s = s.replace(/,(\s*[}\]])/g, '$1');

    // 4. Add missing commas between a value and the next key
    //    Handles: 'value' nextKey:  and  "value" nextKey:  and  word nextKey:
    s = s.replace(/([\w'"}\]])\s+([a-zA-Z_]\w*\s*:)/g, '$1, $2');
    s = s.replace(/([\w'"}\]])\s+("[^"]+"\s*:)/g, '$1, $2');

    // 5. Quote unquoted object keys (word followed by :, not already inside "…")
    //    Only after { or , to avoid matching inside string values
    s = s.replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)(\s*):/g, '$1"$2"$3:');

    // 6. Replace single-quoted string delimiters with double quotes,
    //    treating apostrophes flanked by letters as contractions (not delimiters)
    s = s.replace(/'((?:[^'\\]|\\.|(?<=[a-zA-Z])'(?=[a-zA-Z]))*)'/g,
      (_, inner) => '"' + inner.replace(/"/g, '\\"') + '"');

    // 7. Remove any remaining lone/unmatched single quotes (e.g. trailing ' before ] or })
    s = s.replace(/'(?=\s*[,\]}\s]|$)/g, '');

    // 8a. Remove erroneous = in value position: "key": ="value" → "key": "value"
    s = s.replace(/(":\s*)=\s*/g, '$1');

    // 8b. Remove dangling = before a quoted string at start of line / after comma:
    //     e.g.  \n ="medium"  or  , ="medium"  (model emits value with no key)
    s = s.replace(/([\n,]\s*)=\s*(?=")/g, '$1');

    // 9. Remove dangling keyless values after a comma (value without key in an object).
    //    e.g.  , ="medium"  or  , "medium"  right before }
    //    These appear when the model omits the key name entirely.
    s = s.replace(/,\s*=?\s*"[^"]*"\s*(?=[,}])/g, (match, offset, str) => {
      // Only remove if we are NOT inside an array (heuristic: last open bracket before
      // this position is { not [).  Avoids stripping last element of arrays.
      const before = str.slice(0, offset);
      const lastBrace = before.lastIndexOf('{');
      const lastBracket = before.lastIndexOf('[');
      return lastBrace > lastBracket ? '' : match;
    });

    return s;
  }

  // Truncate at the first fully-balanced { } or [ ] structure, discarding trailing garbage
  balanceJSON(text) {
    let depth = 0;
    let inString = false;
    let escape = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (escape) { escape = false; continue; }
      if (c === '\\' && inString) { escape = true; continue; }
      if (c === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (c === '{' || c === '[') depth++;
      if (c === '}' || c === ']') {
        depth--;
        if (depth === 0) return text.slice(0, i + 1);
      }
    }
    return text;
  }

  // Seal a truncated JSON string by closing any open string, adding a missing value
  // after a bare key, then appending matching closing brackets from the stack
  closeJSON(text) {
    const stack = [];
    let inString = false;
    let escape = false;

    for (const c of text) {
      if (escape) { escape = false; continue; }
      if (c === '\\' && inString) { escape = true; continue; }
      if (c === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (c === '{') stack.push('}');
      else if (c === '[') stack.push(']');
      else if (c === '}' || c === ']') stack.pop();
    }

    let suffix = '';
    if (inString) suffix += '"';                             // close the open string literal
    if ((text + suffix).trimEnd().endsWith(':')) suffix += 'null'; // bare key with no value
    suffix += stack.reverse().join('');                      // close all open { and [
    return text + suffix;
  }

  // Try JSON.parse, then with repairs — returns parsed object or null
  // Always tries both the raw text and the sealed (closeJSON) version so truncated
  // responses are handled automatically at every extraction strategy.
  _tryParse(s) {
    if (!s) return null;
    // Pass 1: raw text
    try { return JSON.parse(s); } catch (e) {}
    const repaired = this.repairJSON(s);
    try { return JSON.parse(repaired); } catch (e) {}
    // Pass 1b: balance after repair (catches spurious } that repair removes)
    try { return JSON.parse(this.balanceJSON(repaired)); } catch (e) {}
    // Pass 2: seal any truncation (closeJSON is a no-op on already-complete JSON)
    const sealed = this.closeJSON(s);
    try { return JSON.parse(sealed); } catch (e) {}
    try { return JSON.parse(this.repairJSON(sealed)); } catch (e) {}
    return null;
  }

  // Last-resort: extract each top-level field individually from broken JSON
  _partialExtract(text) {
    const obj = {};
    // Simple scalar values: strings, numbers, booleans, null
    const scalarRe = /"([^"]+)"\s*:\s*("(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null)/g;
    let m;
    while ((m = scalarRe.exec(text)) !== null) {
      try { obj[m[1]] = JSON.parse(m[2]); } catch (e) {}
    }
    // Complex values: objects { } and arrays [ ]
    const complexRe = /"([^"]+)"\s*:\s*([\[{])/g;
    while ((m = complexRe.exec(text)) !== null) {
      const key = m[1];
      if (obj[key] !== undefined) continue;
      const startIdx = m.index + m[0].length - 1;
      const slice = this.balanceJSON(text.slice(startIdx));
      const parsed = this._tryParse(slice);
      if (parsed !== null) obj[key] = parsed;
    }
    return Object.keys(obj).length > 0 ? obj : null;
  }

  // Robustly extract JSON from a raw API response string/object
  extractJSON(raw) {
    if (!raw) return null;

    // If already a plain parsed object (not a string wrapper), return it directly
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      const inner = raw?.answer ?? raw?.response ?? raw?.data;
      if (inner !== undefined) {
        // inner is already a parsed object — no string parsing needed
        if (typeof inner === 'object' && inner !== null && !Array.isArray(inner)) return inner;
        if (Array.isArray(inner)) return inner;
        // inner is a string — fall through to string parsing below
        return this.extractJSON(inner);
      }
      // raw itself has no wrapper key: it IS the data
      return raw;
    }

    const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
    if (!text) return null;

    let r;

    // Strategy 1: markdown code block (```json ... ```)
    const codeBlock = text.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (codeBlock) {
      r = this._tryParse(codeBlock[1].trim());
      if (r) return r;
    }

    // Strategy 2: extraction starting from first {
    // Pass raw slice — _tryParse will repair then balance internally
    const braceStart = text.indexOf('{');
    if (braceStart !== -1) {
      r = this._tryParse(text.slice(braceStart));
      if (r) return r;
    }

    // Strategy 3: extraction starting from first [
    // Only when [ precedes any { (avoids returning inner array instead of outer object)
    const bracketStart = text.indexOf('[');
    if (bracketStart !== -1 && (braceStart === -1 || bracketStart < braceStart)) {
      r = this._tryParse(text.slice(bracketStart));
      if (r) return r;
    }

    // Strategy 4: greedy span — { ... last } (catches models that append text after closing brace)
    const greedyBrace = text.match(/\{[\s\S]*\}/);
    if (greedyBrace) {
      r = this._tryParse(greedyBrace[0]);
      if (r) return r;
    }

    // Strategy 5: truncated response — seal open strings/brackets then parse
    if (braceStart !== -1) {
      const closed = this.closeJSON(text.slice(braceStart));
      r = this._tryParse(closed);
      if (r) return r;
    }

    // Strategy 6: partial field-by-field extraction (last resort — returns incomplete but usable object)
    return this._partialExtract(text);
  }

  getTranscriptionSocketUrl() {
    return buildApiUrl('SOCKET', '');
  }

  downsampleBuffer(buffer, sampleRate, targetSampleRate) {
    if (targetSampleRate === sampleRate) return buffer;
    const sampleRateRatio = sampleRate / targetSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < newLength) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      let accum = 0, count = 0;
      for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
        accum += buffer[i];
        count++;
      }
      result[offsetResult] = count ? accum / count : 0;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }
    return result;
  }

  floatTo16BitPCM(input) {
    const output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      let s = Math.max(-1, Math.min(1, input[i]));
      output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return output.buffer;
  }

  /**
   * Query the RAG agent with a document collection and question
   */
  async queryRAG(documentCollection, question) {
    const cacheKey = `fraud_typology_34306499::${question}`;
    if (this.ragQueryCache[cacheKey]) {
      return this.ragQueryCache[cacheKey];
    }

    try {
      const formData = new FormData();
      formData.append('query', question);
      formData.append('collection', 'fraud_typology_34306499');
      formData.append('agent_id', 'fraud_detection_agent');
      formData.append('cust_id', 'cust_fraud');
      formData.append('advisor_id', this.storedUserId);
      formData.append('use_case_id', 'fraud_intake');

      const response = await fetch(buildApiUrl('RAG', '/rag_base'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error(`RAG API responded with status: ${response.status}`);

      const data = await response.json();
      const result = { success: true, data };
      this.ragQueryCache[cacheKey] = result;
      return result;
    } catch (error) {
      console.error('Error querying RAG:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Analyze transcript using the Transcript Analysis (Jamba) agent
   */
  async analyzeTranscript(transcript, promptTemplate, promptVariables = {}) {
    try {
      let finalPrompt = promptTemplate;
      Object.entries(promptVariables).forEach(([key, value]) => {
        finalPrompt = finalPrompt.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          typeof value === 'object' ? JSON.stringify(value) : String(value)
        );
      });

      const transcriptionFile = new Blob([transcript], { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', transcriptionFile, 'transcript.txt');
      formData.append('prompt', finalPrompt);

      const response = await fetch(buildApiUrl('JAMBA', '/custom-analyze'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error(`Analysis API responded with status: ${response.status}`);

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error analyzing transcript:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Upload documents to a RAG collection
   */
  async uploadDocuments(files, collectionName) {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('collection_name', 'fraud_typology_34306499');

      const response = await axios.post(buildApiUrl('UPLOAD', '/upload-documents'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error uploading documents:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Two-stage hybrid fraud detection: RAG + Transcript Analysis
   */
  async detectFraudType(transcript, fraudTypologyCollection = FRAUD_TYPOLOGY_COLLECTION, isFraudCall = false) {
    try {
      // Stage 1: RAG retrieves candidate fraud typologies
      const ragQuery = `A bank customer has called the fraud department to report a fraud or scam incident. Based on this conversation excerpt, what fraud typologies are most likely? Identify relevant fraud signals and patterns: ${transcript.slice(0, 1500)}`;
      const ragResult = await this.queryRAG(fraudTypologyCollection, ragQuery);
      console.log(">>marta1:", ragResult);

      let ragCandidates = 'Unknown';
      let ragSignals = 'No signals retrieved';

      if (ragResult.success && ragResult.data) {
        const ragText = typeof ragResult.data === 'string'
          ? ragResult.data
          : (ragResult.data.answer || ragResult.data.response || JSON.stringify(ragResult.data));
        ragCandidates = ragText;
        ragSignals = ragText;
      }

      // Stage 2: Transcript Analysis with RAG context
      const relevancePrefix = isFraudCall
        ? `You are a Fraud Detection AI Agent working in a bank's fraud department. The transcript below is an inbound call to the bank in which a client is reporting a fraud or scam incident to a customer service agent. The agent answers the call first with a standard greeting identifying themselves and the institution — this is normal and must never be treated as a fraud signal or evidence of impersonation. Proceed directly with the full typology analysis based solely on what the CLIENT reports.`
        : `You are a Fraud Detection AI Agent working in a bank's fraud department. The transcript below is an inbound call to the bank between a customer service agent and a client.

IMPORTANT — CALL STRUCTURE: The bank agent always speaks first, greeting the caller and identifying themselves and the institution (e.g. "Thank you for calling Harbor Federal Credit Union, this is Michael, how may I help you?"). This opening is a normal, legitimate bank greeting and must NEVER be flagged as impersonation, a fraud signal, or transcript evidence of any kind. Only analyse what the CLIENT says when identifying fraud signals.

Determine whether the client is reporting a fraud or scam incident — this includes any mention of unauthorized transactions, stolen or compromised cards, account takeover, scam payments, suspicious activity, disputed charges, identity theft, or any other financial crime, even if the conversation is fragmented or still in progress.

Only if the transcript has absolutely NO connection to fraud or financial crime (e.g. a general inquiry, address change, or unrelated topic), respond ONLY with:
{"not_applicable": true, "reason": "brief explanation"}

If the client IS reporting fraud or a scam, perform the full analysis below based on what the CLIENT describes.`;
      const fraudPrompt = `${relevancePrefix}

CONTEXT FROM KNOWLEDGE BASE: {rag_candidates}
RELEVANT FRAUD SIGNALS: {rag_signals}

FRAUD TYPOLOGIES:
- Account Takeover (ATO) — Client reports loss of account access, password reset they didn't initiate, new device logins, contact details changed without their knowledge, or unfamiliar payees added
- Authorized Push Payment (APP) Scam — Client was deceived into authorizing a payment themselves, typically via impersonation of a trusted entity (bank, HMRC, police, utility), romance scam, or investment scam. Impersonation here refers to a THIRD PARTY pretending to be the bank to the client — not the legitimate bank agent on this call.
- Card Not Present (CNP) — Unauthorized charges at online merchants, client still has their physical card, or multiple unfamiliar online transactions in quick succession
- Identity Theft / New Account Fraud — Client reports accounts or credit lines opened in their name that they did not authorize; stolen personal credentials
- Check Fraud — Altered or counterfeit checks, forged signatures, fraudulent remote deposits, unexpected check clearances
- Merchant Dispute — Client recognizes the merchant but disputes the charge due to non-delivery, poor quality, or billing error (lower fraud signal, may be a chargeback)
- First-Party Fraud — Internal signal only; client claim is inconsistent with account data (same device used, item delivered, claim filed shortly after purchase)
- Organized Fraud / Fraud Ring — Multiple coordinated claims, shared device or IP indicators, unusually similar fraud patterns across accounts
- BEC (Business Email Compromise / ATO + Wire) — Client authorized a high-value wire transfer to a new beneficiary following email impersonation or social engineering
- ACH Fraud — Unauthorized ACH debits from unknown merchants or payees, often noticed on a bank statement
- Digital Wallet Fraud — Unauthorized enrollment of client's card into a digital wallet (Apple Pay, Google Pay) or unfamiliar device provisioning

STRICT OUTPUT RULES:
- Return ONLY a single JSON object. No markdown, no code blocks, no prose before or after.
- Use EXACTLY the fields listed below. Do NOT add any extra fields (no "reasoning", no scores, no annotations).
- All string values must use double quotes. Numbers must be plain numbers.
- transcript_evidence must only contain quotes from the CLIENT, never from the bank agent's greeting or standard procedural statements.

OUTPUT FORMAT — only when the client IS reporting fraud or a scam:
{
  "primary_typology": {
    "name": "fraud type name",
    "confidence": 85,
    "signals_detected": ["signal1", "signal2"],
    "transcript_evidence": [{"quote": "exact quote from client", "timestamp": "00:02:34", "signal": "signal name"}]
  },
  "alternative_typologies": [{"name": "fraud type", "confidence": 45, "reason": "one sentence"}],
  "rag_alignment": {
    "matches_rag_prediction": true,
    "rag_candidates_considered": ["type1", "type2"],
    "additional_insights": "one sentence or empty string"
  },
  "red_flags": ["flag1", "flag2"],
  "missing_information": ["item1", "item2"],
  "recommended_questions": ["question1", "question2"]
}

TRANSCRIPT: {transcript}`;

      const analysisResult = await this.analyzeTranscript(transcript, fraudPrompt, {
        rag_candidates: ragCandidates,
        rag_signals: ragSignals,
        transcript
      });
      console.log(">>marta2:", analysisResult);

      if (!analysisResult.success) {
        return { success: false, error: analysisResult.error };
      }

      const parsedResult = this.extractJSON(analysisResult.data);

      if (parsedResult?.not_applicable) {
        return { success: true, not_applicable: true, data: null };
      }

      if (!parsedResult) {
        console.warn('Could not parse fraud detection JSON');
        return { success: false, error: 'Could not parse fraud detection response' };
      }

      return {
        success: true,
        data: parsedResult,
        rawRagResponse: ragResult.data,
        ragUsed: ragResult.success
      };
    } catch (error) {
      console.error('Error detecting fraud type:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Suggest next questions using RAG fed with claim intake questionnaire
   */
  async suggestNextQuestions(transcript, currentFraudType = '', isFraudCall = false) {
    try {
      const preamble = isFraudCall
        ? `You are a Claims Intake Assistant handling a confirmed fraud-related call. Suggest the next 2-3 most relevant questions the agent should ask based on what's already been captured and what's still missing.`
        : `You are a Claims Intake Assistant. First, determine whether the transcript below is fraud-related (any conversation mentioning stolen cards, unauthorized transactions, account compromise, scams, fraud claims, disputed charges, stolen credentials, suspicious activity, or any financial crime — even if partial or unclear).

Only if the transcript has absolutely NO connection to fraud or financial crime, respond ONLY with:
{"not_applicable": true, "reason": "brief explanation"}

If it IS fraud-related, suggest the next 2-3 most relevant questions the agent should ask based on what's already been captured and what's still missing.`;
      const prompt = `${preamble}

Suspected fraud type: ${currentFraudType || 'Unknown'}
Current transcript: ${transcript}

STRICT OUTPUT RULES:
- Return ONLY a single JSON object. No markdown, no code blocks, no prose before or after.
- Use EXACTLY the fields listed below. Do NOT add any extra fields.

OUTPUT FORMAT — only when it IS a fraud call:
{
  "suggested_questions": [
    {
      "category": "category name",
      "question": "the question text",
      "purpose": "one sentence",
      "priority": "high",
      "relevance_reason": "one sentence"
    }
  ]
}`;

      const ragResult = await this.queryRAG(QUESTIONNAIRE_COLLECTION, prompt);

      if (!ragResult.success) {
        return { success: false, error: ragResult.error };
      }

      const parsedResult = this.extractJSON(ragResult.data);

      if (parsedResult?.not_applicable) {
        return { success: true, not_applicable: true, data: null };
      }

      if (!parsedResult) {
        console.warn('Could not parse question suggestions JSON');
        return { success: false, error: 'Could not parse question suggestions response' };
      }

      return { success: true, data: parsedResult };
    } catch (error) {
      console.error('Error suggesting questions:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Extract structured CRM data from transcript
   */
  async extractCRMData(transcript, isFraudCall = false) {
    const preamble = isFraudCall
      ? `This is a confirmed fraud-related call. Extract structured claim data for CRM entry. Map the information to these fields:`
      : `First, determine whether this transcript is fraud-related (any conversation mentioning stolen cards, unauthorized transactions, account compromise, scams, fraud claims, disputed charges, stolen credentials, suspicious activity, or any financial crime — even if partial or unclear).

Only if the transcript has absolutely NO connection to fraud or financial crime, respond ONLY with:
{"not_applicable": true, "reason": "brief explanation"}

If it IS fraud-related, extract structured claim data for CRM entry. Map the information to these fields:`;
    const prompt = `${preamble}

REQUIRED FIELDS:
- customer_name
- account_number
- transaction_date
- transaction_amount
- merchant_name
- transaction_channel (card/online/mobile/wire/check/ACH)

OPTIONAL FIELDS:
- authorization_status (authorized/unauthorized/coerced)
- fraud_type
- claim_narrative (2-3 sentence summary)
- supporting_evidence_offered (list)
- contact_preferences
- account_access_issues (boolean)
- scam_indicators (list)

Mark fields as: "captured", "missing", or "unclear"

STRICT OUTPUT RULES:
- Return ONLY a single JSON object. No markdown, no code blocks, no prose before or after.
- Use EXACTLY the fields listed below. Do NOT add any extra fields.

OUTPUT FORMAT:
{
  "required_fields": {
    "customer_name": {"value": "John Doe", "status": "captured"},
    "account_number": {"value": null, "status": "missing"},
    "transaction_date": {"value": null, "status": "missing"},
    "transaction_amount": {"value": null, "status": "missing"},
    "merchant_name": {"value": null, "status": "missing"},
    "transaction_channel": {"value": null, "status": "missing"}
  },
  "optional_fields": {
    "authorization_status": {"value": null, "status": "missing"},
    "fraud_type": {"value": null, "status": "missing"},
    "claim_narrative": {"value": null, "status": "missing"},
    "supporting_evidence_offered": {"value": [], "status": "missing"},
    "contact_preferences": {"value": null, "status": "missing"},
    "account_access_issues": {"value": null, "status": "missing"},
    "scam_indicators": {"value": [], "status": "missing"}
  },
  "completeness_score": 0,
  "missing_critical_info": [],
  "ready_for_crm": false
}`;

    const result = await this.analyzeTranscript(transcript, prompt);
    if (!result.success) return result;

    const parsedResult = this.extractJSON(result.data);
    if (parsedResult?.not_applicable) return { success: true, not_applicable: true, data: null };
    if (!parsedResult) {
      console.warn('Could not parse CRM data JSON');
      return { success: false, error: 'Could not parse CRM data response' };
    }
    return { success: true, data: parsedResult };
  }

  /**
   * Generate a case summary from the transcript
   */
  async generateCaseSummary(transcript, isFraudCall = false) {
    const preamble = isFraudCall
      ? `This is a confirmed fraud-related call. Generate a concise case summary including:`
      : `First, determine whether this transcript is fraud-related (any conversation mentioning stolen cards, unauthorized transactions, account compromise, scams, fraud claims, disputed charges, stolen credentials, suspicious activity, or any financial crime — even if partial or unclear).

Only if the transcript has absolutely NO connection to fraud or financial crime, respond ONLY with:
{"not_applicable": true, "reason": "brief explanation"}

If it IS fraud-related, generate a concise case summary including:`;
    const prompt = `${preamble}
- Executive summary (2-3 sentences)
- Key facts (bullet points)
- Suspected fraud type and confidence
- Critical evidence mentioned
- Missing information needed to complete claim
- Recommended next steps
- Overall case status (% complete)

STRICT OUTPUT RULES:
- Return ONLY a single JSON object. No markdown, no code blocks, no prose before or after.
- Use EXACTLY the fields listed below. Do NOT add any extra fields.

OUTPUT FORMAT:
{
  "executive_summary": "2-3 sentence summary",
  "key_facts": ["fact1", "fact2"],
  "fraud_assessment": "Fraud Type (85% confidence)",
  "evidence_collected": ["item1", "item2"],
  "missing_information": ["item1", "item2"],
  "next_steps": ["step1", "step2"],
  "completeness_percentage": 70
}`;

    const result = await this.analyzeTranscript(transcript, prompt);
    if (!result.success) return result;

    const parsedResult = this.extractJSON(result.data);
    if (parsedResult?.not_applicable) return { success: true, not_applicable: true, data: null };
    if (!parsedResult) {
      console.warn('Could not parse case summary JSON');
      return { success: false, error: 'Could not parse case summary response' };
    }
    return { success: true, data: parsedResult };
  }

  /**
   * Analyze sentiment of the transcript
   */
  async analyzeSentiment(transcript, isFraudCall = false) {
    const preamble = isFraudCall
      ? `This is a confirmed fraud-related call. Analyze the sentiment and emotional tone of the transcript.\n\nAnalyze:`
      : `First, determine whether this transcript is fraud-related (any conversation mentioning stolen cards, unauthorized transactions, account compromise, scams, fraud claims, disputed charges, stolen credentials, suspicious activity, or any financial crime — even if partial or unclear).

Only if the transcript has absolutely NO connection to fraud or financial crime, respond ONLY with:
{"not_applicable": true, "reason": "brief explanation"}

If it IS fraud-related, analyze the sentiment and emotional tone of the transcript.

Analyze:`;
    const prompt = `${preamble}
- Overall call sentiment (positive/neutral/negative)
- Customer sentiment: emotional state, stress level, urgency
- Agent sentiment: tone, empathy level, professionalism
- Sentiment trend throughout the call (improving/declining/stable)
- Key emotional moments (frustration, confusion, relief, etc.)
- Customer vulnerability signals
- Escalation risk

STRICT OUTPUT RULES:
- Return ONLY a single JSON object. No markdown, no code blocks, no prose before or after.
- Use EXACTLY the fields listed below. Do NOT add any extra fields.
- "overall_sentiment": one of "positive", "neutral", "negative"
- "stress_level" and "urgency": one of "low", "moderate", "high"
- "trend": one of "improving", "stable", "declining"
- "severity": one of "low", "medium", "high"
- "escalation_risk": one of "low", "medium", "high"
- All scores are numbers between -1.0 and 1.0

OUTPUT FORMAT:
{
  "overall_sentiment": "negative",
  "overall_score": -0.6,
  "customer": {
    "sentiment": "distressed",
    "stress_level": "high",
    "urgency": "high",
    "emotions": ["anxious", "confused", "frustrated"],
    "score": -0.7
  },
  "trend": "stable",
  "trend_description": "one sentence",
  "key_moments": [
    {"timestamp": "00:02:10", "emotion": "frustration", "quote": "exact quote", "severity": "high"}
  ],
  "vulnerability_signals": ["signal1", "signal2"],
  "escalation_risk": "medium"
}`;

    const result = await this.analyzeTranscript(transcript, prompt);
    console.log("marta3: ", result);
    if (!result.success) return result;

    const parsedResult = this.extractJSON(result.data);
    if (parsedResult?.not_applicable) return { success: true, not_applicable: true, data: null };
    if (!parsedResult) {
      console.warn('Could not parse sentiment JSON');
      return { success: false, error: 'Could not parse sentiment response' };
    }
    return { success: true, data: parsedResult };
  }

  /**
   * Generate a structured CRM entry from the transcript using the transcript analysis agent
   */
  async generateCRMEntry(transcript) {
    const prompt = `You are an AI assistant supporting a bank's fraud and scam claims intake team. The transcript below is a call between a customer service agent and a client who is reporting a fraud or scam incident. Extract structured information strictly from what was said to populate a CRM entry.

Return the output as a JSON object with EXACTLY these four fields:
- "client_goals": A short, factual summary of what the client is asking the agent to do — for example, dispute an unauthorized charge, freeze or close a compromised account, block a card, file a fraud report, or recover lost funds.
- "client_concerns": A short, factual summary of the specific worries or risks the client raised — for example, fear of further unauthorized activity, potential identity theft, concern about account security, or anxiety about pending transactions.
- "preferences": A short summary of any resolution or communication preferences the client explicitly stated — for example, preferred contact method, urgency of resolution, whether they want to keep or cancel the account, or specific outcome expectations.
- "advisor_action_items": A list of concrete, clearly stated follow-up tasks the agent committed to or that are required based on the call — for example, initiating a chargeback, placing a temporary account freeze, escalating to the fraud investigation team, sending a new card, or scheduling a callback. Each item must be directly grounded in the conversation.

Do not infer, assume, or include anything not explicitly discussed during the call. If a field has no relevant information in the transcript, set its value to null.

STRICT OUTPUT RULES:
- Return ONLY a single JSON object. No markdown, no code blocks, no prose before or after.
- Use EXACTLY the four fields listed above. Do not add extra fields.

OUTPUT FORMAT:
{
  "client_goals": "short factual summary or null",
  "client_concerns": "short factual summary or null",
  "preferences": "short factual summary or null",
  "advisor_action_items": ["action item 1", "action item 2"]
}

TRANSCRIPT:
${transcript}`;

    const result = await this.analyzeTranscript(transcript, prompt);
    if (!result.success) return result;

    const parsedResult = this.extractJSON(result.data);
    if (!parsedResult) {
      console.warn('Could not parse CRM entry JSON');
      return { success: false, error: 'Could not parse CRM entry response' };
    }
    return { success: true, data: parsedResult };
  }

  /**
   * Push claim data to CRM
   */
  async pushToCRM(claimData) {
    try {
      const response = await fetch(buildApiUrl('AGENTS', '/crm/push-claim'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(claimData)
      });

      if (!response.ok) throw new Error(`CRM API responded with status: ${response.status}`);
      return { success: true, data: await response.json() };
    } catch (error) {
      console.error('Error pushing to CRM:', error);
      return { success: false, error: error.message };
    }
  }

  clearRAGCache() {
    this.ragQueryCache = {};
  }
}

const customerServiceDashboardService = new CustomerServiceDashboardService();
export default customerServiceDashboardService;
